// Location: src/pages/plan-summary/components/NextStepsCard.tsx

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '../../../stores/uiStore';

interface NextStepsCardProps {
  goalAchieved: boolean;
  adherence: number;
  planGoal: string;
}

export default function NextStepsCard({ 
  goalAchieved, 
  adherence,
}: NextStepsCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openChatBot } = useUIStore();

  const steps = [
    {
      id: 'new_plan',
      title: t('planSummary.nextSteps.actions.newPlan.title'),
      description: goalAchieved 
        ? t('planSummary.nextSteps.actions.newPlan.descriptionAchieved')
        : t('planSummary.nextSteps.actions.newPlan.descriptionContinue'),
      icon: 'ri-rocket-2-line',
      color: 'text-yellow-400',
      action: () => openChatBot(t('planSummary.prompts.newPlan'))
    },
    {
      id: 'maintenance',
      title: t('planSummary.nextSteps.actions.maintenance.title'),
      description: goalAchieved
        ? t('planSummary.nextSteps.actions.maintenance.descriptionAchieved')
        : t('planSummary.nextSteps.actions.maintenance.descriptionContinue'),
      icon: 'ri-anchor-line',
      color: 'text-green-400',
      action: () => openChatBot(t('planSummary.prompts.maintenance'))
    },
    {
      id: 'adjust',
      title: t('planSummary.nextSteps.actions.adjust.title'),
      description: adherence < 70
        ? t('planSummary.nextSteps.actions.adjust.descriptionLow')
        : t('planSummary.nextSteps.actions.adjust.descriptionHigh'),
      icon: 'ri-equalizer-line',
      color: 'text-blue-400',
      action: () => openChatBot(t('planSummary.prompts.adjustPlan'))
    }
  ];

  const getPersonalizedMessage = () => {
    if (goalAchieved) {
      return {
        title: t('planSummary.nextSteps.message.goalAchieved.title'),
        description: t('planSummary.nextSteps.message.goalAchieved.description')
      };
    } else if (adherence >= 70) {
      return {
        title: t('planSummary.nextSteps.message.goodAdherence.title'),
        description: t('planSummary.nextSteps.message.goodAdherence.description', { 
          adherence: Math.round(adherence) 
        })
      };
    } else {
      return {
        title: t('planSummary.nextSteps.message.needsImprovement.title'),
        description: t('planSummary.nextSteps.message.needsImprovement.description', { 
          adherence: Math.round(adherence) 
        })
      };
    }
  };

  const message = getPersonalizedMessage();

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-6 lg:p-10 backdrop-blur-sm relative overflow-hidden">
      {/* Background flourish */}
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-white/[0.01] rounded-full blur-[100px] pointer-events-none translate-x-1/3 translate-y-1/3"></div>

      <div className="relative z-10 max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-light text-white tracking-tight mb-4">{t('planSummary.nextSteps.title')}</h2>
          
          {/* Personalized Message wrapped in elegant minimal border */}
          <div className="inline-flex flex-col items-center justify-center p-6 border border-white/5 rounded-2xl bg-white/[0.01] max-w-2xl mx-auto">
            <div className="text-sm font-medium text-white mb-2">{message.title}</div>
            <div className="text-gray-400 text-sm font-light leading-relaxed">{message.description}</div>
          </div>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {steps.map((step) => (
            <button
              key={step.id}
              onClick={step.action}
              className="group bg-white/[0.02] border border-white/5 hover:border-white/20 hover:bg-white/[0.04] rounded-2xl p-6 text-left transition-all duration-300 cursor-pointer flex flex-col h-full"
            >
              <div className={`w-10 h-10 rounded-full bg-white/5 flex items-center justify-center mb-5 ${step.color}`}>
                <i className={`${step.icon} text-xl`}></i>
              </div>
              
              <div className="text-white font-medium tracking-tight mb-2">
                {step.title}
              </div>
              
              <div className="text-gray-500 text-xs font-light leading-relaxed flex-grow">
                {step.description}
              </div>

              <div className="mt-6 pt-4 border-t border-white/5 flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-400 font-medium group-hover:text-white transition-colors">
                <span>{t('planSummary.nextSteps.actions.getStarted')}</span>
                <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
              </div>
            </button>
          ))}
        </div>

        {/* Quick Footer Actions */}
        <div className="flex flex-wrap items-center justify-center gap-6 pt-8 border-t border-white/5">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest mr-2">{t('planSummary.nextSteps.quickActions.title')}</span>
          
          <button onClick={() => navigate('/dashboard')} className="text-xs text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 cursor-pointer">
            <i className="ri-dashboard-line text-sm"></i>
            {t('planSummary.nextSteps.quickActions.dashboard')}
          </button>
          
          <button onClick={() => navigate('/progress')} className="text-xs text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 cursor-pointer">
            <i className="ri-line-chart-line text-sm"></i>
            {t('planSummary.nextSteps.quickActions.progress')}
          </button>
          
          <button onClick={() => openChatBot(t('planSummary.prompts.askAboutSummary'))} className="text-xs text-yellow-500/80 hover:text-yellow-400 transition-colors flex items-center gap-1.5 cursor-pointer">
            <i className="ri-chat-smile-3-line text-sm"></i>
            {t('planSummary.nextSteps.quickActions.askBot')}
          </button>
        </div>
      </div>
    </div>
  );
}